<template>
  <div class="flex justify-between items-center">
    <component
      :is="page > 0 ? NuxtLink : 'button'"
      :to="page > 0 ? doGetPageLink(page - 1) : undefined"
      :disabled="page === 0"
      class="button show-icon"
    >
      <SpriteSymbol name="arrow-left" />
      <span class="hidden md:inline">{{ $texts('back', 'Zurück') }}</span>
    </component>
    <div class="flex font-medium items-center">
      <NuxtLink
        v-for="p in beginningPages"
        :key="p"
        :to="doGetPageLink(p - 1)"
        class="pagination-page"
        :class="{
          'is-active': p - 1 === page,
          'is-previous': p - 1 === page - 1,
          'is-next': p - 1 === page + 1,
        }"
      >
        {{ p }}</NuxtLink
      >

      <div v-if="hasBeginningGap" class="pagination-page" aria-hidden="true">
        &hellip;
      </div>
      <NuxtLink
        v-for="p in slidingWindowPages"
        :key="p"
        :to="doGetPageLink(p - 1)"
        :active="p - 1 === page"
        :aria-label="p"
        class="pagination-page"
        :class="{
          'is-active': p - 1 === page,
          'is-previous': p - 1 === page - 1,
          'is-next': p - 1 === page + 1,
        }"
        >{{ p }}
      </NuxtLink>
      <div v-if="hasEndingGap" aria-hidden="true" class="pagination-page">
        &hellip;
      </div>

      <NuxtLink
        v-for="p in endingPages"
        :key="p"
        :to="doGetPageLink(p - 1)"
        :active="p - 1 === page"
        class="pagination-page"
        :class="{
          'is-active': p - 1 === page,
          'is-previous': p - 1 === page - 1,
          'is-next': p - 1 === page + 1,
        }"
        >{{ p }}
      </NuxtLink>
    </div>

    <component
      :is="page < totalPages - 1 ? NuxtLink : 'button'"
      :to="page < totalPages - 1 ? doGetPageLink(page + 1) : undefined"
      :disabled="page > totalPages - 2"
      class="button show-icon"
    >
      <span class="hidden md:inline">{{ $texts('continue', 'Weiter') }}</span>
      <SpriteSymbol name="arrow-right" />
    </component>
  </div>
</template>

<script lang="ts" setup>
import type { RouteLocationRaw } from 'vue-router'
import { NuxtLink } from '#components'

const route = useRoute()
const { $texts } = useEasyTexts()

function range(start: number, end: number) {
  const r: number[] = []

  if (typeof start !== 'number' || typeof end !== 'number') {
    return r
  }

  if (start > end) {
    const temp = start
    start = end
    end = temp
  }

  for (let i = start; i <= end; i++) {
    r.push(i)
  }

  return r
}

type GetPageLink = (page: number) => RouteLocationRaw

const defaultGetPageLink: GetPageLink = function (page: number) {
  const query = {
    ...route.query,
  }
  if (page <= 0) {
    delete query.page
    return {
      name: route.name!,
      params: route.params,
      query,
    }
  }

  return {
    name: route.name!,
    params: route.params,
    query: {
      ...query,
      page,
    },
  }
}

const props = withDefaults(
  defineProps<{
    page: number
    totalPages: number
    nonSlidingSize?: number
    getPageLink?: GetPageLink | null
  }>(),
  { nonSlidingSize: 5, getPageLink: null },
)

const doGetPageLink = props.getPageLink || defaultGetPageLink

const isSliding = computed(() => {
  return props.totalPages > props.nonSlidingSize
})

const slidingEndingSize = 1
const slidingWindowSize = 7

const beginningPages = computed(() => {
  return range(1, isSliding.value ? slidingEndingSize : props.totalPages)
})

const lastBeginningPage = computed(() => {
  return beginningPages.value[beginningPages.value.length - 1]
})

const firstWindowPage = computed(() => {
  return slidingWindowPages.value[0]
})

const endingPages = computed(() => {
  if (!isSliding.value) {
    return []
  }

  return range(props.totalPages - slidingEndingSize + 1, props.totalPages)
})

const firstEndingPage = computed(() => {
  return endingPages.value[0]
})

const slidingWindowHalf = computed(() => {
  let half = slidingWindowSize / 2

  if (slidingWindowSize % 2 === 1) {
    half -= 0.5
  }

  return half
})

const slidingWindowPages = computed(() => {
  if (!isSliding.value) {
    return []
  }

  const startOffset = lastBeginningPage.value + slidingWindowHalf.value
  const endOffset = firstEndingPage.value - slidingWindowHalf.value

  if (props.page <= startOffset) {
    return range(
      lastBeginningPage.value + 1,
      lastBeginningPage.value + slidingWindowSize,
    )
  }

  if (props.page > startOffset && props.page < endOffset) {
    let upperHalfForEvenWindowSizes = slidingWindowHalf.value
    if (slidingWindowSize % 2 === 0) {
      upperHalfForEvenWindowSizes /= 2
    }

    return range(
      -slidingWindowHalf.value + props.page,
      upperHalfForEvenWindowSizes + props.page,
    )
  }

  return range(
    firstEndingPage.value - slidingWindowSize,
    firstEndingPage.value - 1,
  )
})

const hasBeginningGap = computed(() => {
  if (!isSliding.value) {
    return false
  }

  return lastBeginningPage.value + 1 !== firstWindowPage.value
})

const lastWindowPage = computed(() => {
  return slidingWindowPages.value[slidingWindowPages.value.length - 1]
})

const hasEndingGap = computed(() => {
  if (!isSliding.value) {
    return false
  }

  return lastWindowPage.value + 1 !== firstEndingPage.value
})
</script>

<style lang="postcss">
.pagination-page {
  @apply w-[30px] h-[30px] flex items-center justify-center rounded-full md:w-[40px] md:h-[40px]  mobile-only:rounded-full;
  @apply text-xs md:text-sm lg:text-base;

  &.is-active {
    @apply bg-group-500 text-white font-bold;
  }

  &:is(a) {
    &:not(.is-active) {
      &:hover {
        @apply bg-gray-50;
      }
    }
  }
}

a.pagination-page {
  &:not(.is-active):not(.is-next):not(.is-previous):not(:first-child):not(
      :last-child
    ) {
    @apply mobile-only:hidden;
  }
}
</style>
